import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Star, Send, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ReviewSubmitSection = () => {
  const { isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 5,
    title: '',
    content: '',
    program_name: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [serviceOptions, setServiceOptions] = useState([]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const [sessionsRes, programsRes] = await Promise.allSettled([
          fetch(`${backendUrl}/api/cms/sessions`),
          fetch(`${backendUrl}/api/cms/programs`)
        ]);

        const options = [];
        const seen = new Set();

        if (sessionsRes.status === 'fulfilled' && sessionsRes.value.ok) {
          const sessions = await sessionsRes.value.json();
          sessions.forEach(s => {
            const label = isRTL ? (s.arabic_title || s.title_ar || s.title) : s.title;
            const val = s.title;
            if (!seen.has(val)) { seen.add(val); options.push({ value: val, label, group: 'session' }); }
          });
        }

        if (programsRes.status === 'fulfilled' && programsRes.value.ok) {
          const programs = await programsRes.value.json();
          programs.forEach(p => {
            const label = isRTL ? (p.arabic_title || p.title) : p.title;
            const val = p.title;
            if (val && !seen.has(val)) { seen.add(val); options.push({ value: val, label, group: 'program' }); }
          });
        }

        setServiceOptions(options);
      } catch (err) {
        console.error('ReviewSubmitSection: error fetching services', err);
      }
    };
    fetchServices();
  }, [isRTL, backendUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${backendUrl}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success(isRTL ? 'شكراً لمراجعتك!' : 'Thank you for your review!');
        setTimeout(() => {
          setFormData({ name: '', email: '', rating: 5, title: '', content: '', program_name: '' });
          setIsSubmitted(false);
        }, 3000);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to submit review');
      }
    } catch (err) {
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="section-padding bg-charcoal-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-2xl">

        {/* Section header — consistent with all other sections */}
        <div className="text-center mb-10 sm:mb-14">
          <p className="text-gold text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-3 sm:mb-4">
            {isRTL ? 'قصص النجاح' : 'Your Experience'}
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-cream leading-tight mb-4 sm:mb-6">
            {isRTL ? 'شاركينا تجربتك' : 'Share Your Experience'}
          </h2>
          <div className="gold-divider" />
          <p className="text-cream-300 mt-6 text-sm sm:text-base">
            {isRTL
              ? 'نود أن نسمع عن رحلتك معنا'
              : 'We would love to hear about your journey with us'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-charcoal border border-gold/15 rounded-2xl p-6 sm:p-8 space-y-5">

          {/* Rating */}
          <div className="text-center">
            <label className="block text-gold text-xs uppercase tracking-wider mb-3">
              {isRTL ? 'تقييمك' : 'Your Rating'}
            </label>
            <div className="flex items-center justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData({ ...formData, rating: star })}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star
                    size={30}
                    className={`transition-colors ${
                      star <= (hoveredRating || formData.rating)
                        ? 'text-gold fill-gold'
                        : 'text-cream-500'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Name & Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-gold text-xs uppercase tracking-wider mb-2">
                {isRTL ? 'الاسم' : 'Your Name'} *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={isRTL ? 'أدخل اسمك' : 'Enter your name'}
                className="w-full px-4 py-3 bg-charcoal-100 border border-white/10 rounded-lg text-cream text-sm focus:border-gold focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-gold text-xs uppercase tracking-wider mb-2">
                {isRTL ? 'البريد الإلكتروني' : 'Email'}{' '}
                <span className="text-cream-400 normal-case tracking-normal">({isRTL ? 'اختياري' : 'optional'})</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={isRTL ? 'بريدك@الإلكتروني.com' : 'your@email.com'}
                className="w-full px-4 py-3 bg-charcoal-100 border border-white/10 rounded-lg text-cream text-sm focus:border-gold focus:outline-none transition-colors"
              />
            </div>
          </div>

          {/* Service / Program */}
          <div>
            <label className="block text-gold text-xs uppercase tracking-wider mb-2">
              {isRTL ? 'الخدمة / البرنامج' : 'Service / Program'}{' '}
              <span className="text-cream-400 normal-case tracking-normal">({isRTL ? 'اختياري' : 'optional'})</span>
            </label>
            <select
              value={formData.program_name}
              onChange={(e) => setFormData({ ...formData, program_name: e.target.value })}
              className="w-full px-4 py-3 bg-charcoal-100 border border-white/10 rounded-lg text-cream text-sm focus:border-gold focus:outline-none transition-colors cursor-pointer"
            >
              <option value="">{isRTL ? 'اختر خدمة...' : 'Select a service...'}</option>
              {serviceOptions.filter(o => o.group === 'session').length > 0 && (
                <optgroup label={isRTL ? 'الجلسات' : 'Sessions'}>
                  {serviceOptions.filter(o => o.group === 'session').map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </optgroup>
              )}
              {serviceOptions.filter(o => o.group === 'program').length > 0 && (
                <optgroup label={isRTL ? 'البرامج' : 'Programs'}>
                  {serviceOptions.filter(o => o.group === 'program').map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </optgroup>
              )}
              <option value="Other">{isRTL ? 'أخرى' : 'Other'}</option>
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-gold text-xs uppercase tracking-wider mb-2">
              {isRTL ? 'عنوان المراجعة' : 'Review Title'}{' '}
              <span className="text-cream-400 normal-case tracking-normal">({isRTL ? 'اختياري' : 'optional'})</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={isRTL ? 'ملخص قصير' : 'A brief summary'}
              className="w-full px-4 py-3 bg-charcoal-100 border border-white/10 rounded-lg text-cream text-sm focus:border-gold focus:outline-none transition-colors"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-gold text-xs uppercase tracking-wider mb-2">
              {isRTL ? 'مراجعتك' : 'Your Review'} *
            </label>
            <textarea
              required
              rows={4}
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder={isRTL ? 'شاركينا تجربتك...' : 'Share your experience...'}
              className="w-full px-4 py-3 bg-charcoal-100 border border-white/10 rounded-lg text-cream text-sm focus:border-gold focus:outline-none transition-colors resize-none"
            />
          </div>

          {/* Submit */}
          <div>
            <button
              type="submit"
              disabled={isSubmitting || isSubmitted}
              className="w-full btn-gold flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed active:scale-[0.98]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>{isRTL ? 'جاري الإرسال...' : 'Submitting...'}</span>
                </>
              ) : isSubmitted ? (
                <>
                  <Check size={16} />
                  <span>{isRTL ? 'تم الإرسال!' : 'Submitted!'}</span>
                </>
              ) : (
                <>
                  <Send size={15} />
                  <span>{isRTL ? 'إرسال المراجعة' : 'Submit Review'}</span>
                </>
              )}
            </button>
            <p className="text-cream-400/60 text-xs text-center mt-3">
              {isRTL
                ? 'سيتم مراجعة تقييمك قبل النشر'
                : 'Your review will be moderated before publishing'}
            </p>
          </div>

        </form>
      </div>
    </section>
  );
};

export default ReviewSubmitSection;
